import { HexCoords, Bearing } from '../grid';

// A* pathfinding implementation.
// Based on https://www.redblobgames.com/pathfinding/a-star/implementation.html

export interface PathNode {
  coords: HexCoords;
  bearing: Bearing;
  cost?: number;
}

export type Goal = HexCoords | PathNode;

class PriorityQueue {
  private elements: { item: PathNode, priority: number }[] = [];

  put(item: PathNode, priority: number) {
    this.elements.push({ item, priority });
    this.elements.sort((a, b) => a.priority - b.priority);
  }

  get(): PathNode {
    return this.elements.shift()!.item;
  }

  empty(): boolean {
    return this.elements.length === 0;
  }
}

export default class Pathfinder {
  /**
   * Finds the shortest path from a start node to one of several goal nodes.
   * @param {PathNode} startNode - The starting position, e.g., { coords, bearing }.
   * @param {Goal[]} goals - An array of goals. A goal can be just coordinates (HexCoords) or a full position ({ coords, bearing }).
   * @param {(node: PathNode) => PathNode[]} getNeighborsFn - A function that takes a node and returns its neighbors with their costs.
   * @returns {PathNode[]|null} The path as an array of nodes, or null if no path is found.
   */
  static findPath(startNode: PathNode, goals: Goal[], getNeighborsFn: (node: PathNode) => PathNode[]): PathNode[] | null {
    if (!goals || goals.length === 0) {
      return null;
    }

    const frontier = new PriorityQueue();
    frontier.put(startNode, 0);

    const cameFrom: Map<string, PathNode | null> = new Map(); // key: node.key, value: parent node
    const costSoFar: Map<string, number> = new Map(); // key: node.key, value: g cost

    const startKey = `${startNode.coords.key}:${startNode.bearing}`;
    cameFrom.set(startKey, null);
    costSoFar.set(startKey, 0);

    let goalReached: PathNode | null = null;

    const goalCoords = goals.map(g => g instanceof HexCoords ? g : g.coords);
    const heuristic = (node: PathNode): number => node.coords.absoluteDistanceFromNearest(goalCoords);

    const isGoal = (node: PathNode): boolean => {
      return goals.some(g => {
        const goalCoords = g instanceof HexCoords ? g : g.coords;
        if (!node.coords.equalTo(goalCoords)) {
          return false;
        }
        // If goal is just coords, we're done.
        if (g instanceof HexCoords) {
          return true;
        }
        // If goal is a full position, bearing must match too.
        return node.bearing === (g as PathNode).bearing;
      });
    };

    while (!frontier.empty()) {
      const current = frontier.get();
      const currentKey = `${current.coords.key}:${current.bearing}`;

      if (isGoal(current)) {
        goalReached = current;
        break;
      }

      const neighbors = getNeighborsFn(current);

      for (const next of neighbors) {
        const newCost = costSoFar.get(currentKey)! + (next.cost || 1);
        const nextKey = `${next.coords.key}:${next.bearing}`;

        if (!costSoFar.has(nextKey) || newCost < costSoFar.get(nextKey)!) {
          costSoFar.set(nextKey, newCost);
          const priority = newCost + heuristic(next);
          frontier.put(next, priority);
          cameFrom.set(nextKey, current);
        }
      }
    }

    if (!goalReached) {
      return null;
    }

    // Reconstruct path
    const path: PathNode[] = [];
    let current: PathNode | null = goalReached;
    while (current) {
      path.unshift(current);
      const currentKey: string = `${current.coords.key}:${current.bearing}`;
      current = cameFrom.get(currentKey) || null;
    }

    return path;
  }
}
